import { BASE_TILE_TEXTURE_SPAN, DETAIL_TYPES, TILE_KIND_BASE, TILE_KIND_DETAIL, TILE_TYPES } from './tiles';
import { buildApiUrl } from '../../Api';
import { mediaPath, MEDIA_VARIANTS } from '../../media';

export const DETAIL_RENDER_SCALE_MIN = 0.5;
export const DETAIL_RENDER_SCALE_MAX = 3;
export const DETAIL_RENDER_DIMENSION_MAX = 8;
export const DEFAULT_DETAIL_RENDER_SCALE = 1;

export function buildTileImageUrl(imageId) {
  if (!imageId) return null;
  const trimmed = `${imageId}`.trim().replace(/^\/+/, '');
  if (!trimmed) return null;
  return buildApiUrl(mediaPath(trimmed, MEDIA_VARIANTS.TILE_DETAIL));
}

export function buildWorldAlignedBaseStyle(imageUrl, x, y, tileSize) {
  if (!imageUrl) return {};

  const textureSize = tileSize * BASE_TILE_TEXTURE_SPAN;
  const offsetX = -((x * tileSize) % textureSize);
  const offsetY = -((y * tileSize) % textureSize);

  return {
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: `${textureSize}px ${textureSize}px`,
    backgroundRepeat: 'repeat',
    backgroundPosition: `${offsetX}px ${offsetY}px`,
  };
}

export function buildWorldAlignedTransitionMaskStyle(x, y, tileSize) {
  const maskSize = tileSize * BASE_TILE_TEXTURE_SPAN;
  const offsetX = -((x * tileSize) % maskSize);
  const offsetY = -((y * tileSize) % maskSize);
  const position = `${offsetX}px ${offsetY}px`;
  const size = `${maskSize}px ${maskSize}px`;

  return {
    WebkitMaskPosition: position,
    WebkitMaskSize: size,
    maskPosition: position,
    maskSize: size,
  };
}

export function normalizeTileTags(value) {
  const values = Array.isArray(value) ? value : [value];
  const tags = [];
  const seen = new Set();

  values.forEach((entry) => {
    `${entry ?? ''}`.split(',').forEach((rawTag) => {
      const tag = rawTag.trim().replace(/^#+/, '').trim().toLowerCase();
      if (!tag || seen.has(tag)) return;
      seen.add(tag);
      tags.push(tag);
    });
  });

  return tags;
}

export function normalizeRenderScaleTiles(value, type = TILE_KIND_DETAIL) {
  if (type !== TILE_KIND_DETAIL) {
    return DEFAULT_DETAIL_RENDER_SCALE;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return DEFAULT_DETAIL_RENDER_SCALE;
  }

  return Math.min(
    DETAIL_RENDER_SCALE_MAX,
    Math.max(DETAIL_RENDER_SCALE_MIN, numericValue),
  );
}

export function normalizeRenderDimensionTiles(
  value,
  fallback = DEFAULT_DETAIL_RENDER_SCALE,
  type = TILE_KIND_DETAIL,
) {
  if (type !== TILE_KIND_DETAIL) return DEFAULT_DETAIL_RENDER_SCALE;
  const safeFallback = normalizeRenderScaleTiles(fallback, type);
  if (value == null || value === '') return safeFallback;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return safeFallback;
  return Math.min(
    DETAIL_RENDER_DIMENSION_MAX,
    Math.max(DETAIL_RENDER_SCALE_MIN, numericValue),
  );
}

export function detailRenderSizePixels(tileSize, renderScaleTiles) {
  const numericTileSize = Number(tileSize);
  const safeTileSize = Number.isFinite(numericTileSize) && numericTileSize > 0
    ? numericTileSize
    : 1;
  const scale = normalizeRenderScaleTiles(renderScaleTiles, TILE_KIND_DETAIL);
  return Math.max(1, Math.round(safeTileSize * scale));
}

export function detailRenderDimensionsPixels(
  tileSize,
  renderWidthTiles,
  renderHeightTiles,
  renderScaleTiles = DEFAULT_DETAIL_RENDER_SCALE,
) {
  const numericTileSize = Number(tileSize);
  const safeTileSize = Number.isFinite(numericTileSize) && numericTileSize > 0
    ? numericTileSize
    : 1;
  return {
    width: Math.max(1, Math.round(safeTileSize * normalizeRenderDimensionTiles(
      renderWidthTiles,
      renderScaleTiles,
    ))),
    height: Math.max(1, Math.round(safeTileSize * normalizeRenderDimensionTiles(
      renderHeightTiles,
      renderScaleTiles,
    ))),
  };
}

export function normalizeTileData(tile, fallbackType = TILE_KIND_BASE) {
  if (!tile) return null;

  const id = tile.id || tile.Id || tile._id || tile.key || tile.Key || tile.name || tile.Name;
  if (!id) return null;

  const key = tile.key || tile.Key || id;
  const label = tile.label || tile.name || tile.Name || key;
  const rawType = tile.type || tile.Type || fallbackType;
  const type = rawType === TILE_KIND_DETAIL ? TILE_KIND_DETAIL : TILE_KIND_BASE;
  const fallbackColor = tile.fallbackColor || tile.FallbackColor || (type === TILE_KIND_BASE ? '#6b7280' : '#94a3b8');
  const imageId = tile.imageId || tile.ImageId || tile.image || tile.Image || null;
  const walkable = tile.walkable ?? tile.Walkable ?? true;
  const category = tile.category || tile.Category || '';
  const tags = normalizeTileTags(tile.tags || tile.Tags || []);
  const renderScaleTiles = normalizeRenderScaleTiles(
    tile.renderScaleTiles ?? tile.RenderScaleTiles ?? DEFAULT_DETAIL_RENDER_SCALE,
    type,
  );
  const renderWidthTiles = normalizeRenderDimensionTiles(
    tile.renderWidthTiles ?? tile.RenderWidthTiles,
    renderScaleTiles,
    type,
  );
  const renderHeightTiles = normalizeRenderDimensionTiles(
    tile.renderHeightTiles ?? tile.RenderHeightTiles,
    renderScaleTiles,
    type,
  );

  return {
    id,
    key,
    label,
    type,
    fallbackColor,
    imageId,
    walkable: type === TILE_KIND_BASE ? !!walkable : true,
    category,
    tags,
    renderScaleTiles,
    renderWidthTiles,
    renderHeightTiles,
  };
}

export function getDefaultBaseTiles() {
  return Object.values(TILE_TYPES)
    .map((tile) => normalizeTileData(tile, TILE_KIND_BASE))
    .filter(Boolean);
}

export const NONE_DETAIL_OPTION = {
  id: 'none',
  key: 'none',
  label: 'None',
  type: 'none',
  fallbackColor: 'transparent',
  imageId: null,
  imageUrl: null,
  walkable: true,
  category: '',
  renderScaleTiles: DEFAULT_DETAIL_RENDER_SCALE,
  renderWidthTiles: DEFAULT_DETAIL_RENDER_SCALE,
  renderHeightTiles: DEFAULT_DETAIL_RENDER_SCALE,
};

export function getDefaultDetailTiles() {
  return [
    NONE_DETAIL_OPTION,
    ...Object.values(DETAIL_TYPES)
      .map((detail) => normalizeTileData(detail, TILE_KIND_DETAIL))
      .filter(Boolean),
  ];
}
